import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Box, Typography, Alert, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const ACCEPTED_FORMATS = 'PDF, Word (DOCX), Text (TXT), Spreadsheets (XLSX, XLS)';
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface FileUploadProps {
    files: FileList | null;
    disabled: boolean;
    setFiles: (files: FileList | null) => void;
}

export const FileUpload = ({ files, disabled, setFiles }: FileUploadProps) => {
    const { t } = useTranslation();
    const [error, setError] = useState<string | null>(null);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files;
        setError(null);

        if (selectedFiles && selectedFiles.length > 0) {
            // Only allow one file
            if (selectedFiles.length > 1) {
                setError(t('web_app.file_upload.only_one_file_allowed'));
                setFiles(null);
                return;
            }

            const file = selectedFiles[0];
            const validTypes = [
                'application/pdf',
                // Word
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                // Excel
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel',
                // Text
                'text/plain',
            ];

            // Validate file type
            if (!validTypes.includes(file.type)) {
                setError(`${t('web_app.file_upload.invalid_files_error')}: ${file.name}`);
                setFiles(null);
                return;
            }

            // Validate file size
            if (file.size > MAX_FILE_SIZE_BYTES) {
                setError(
                    t('web_app.file_upload.file_too_large', {
                        maxSize: MAX_FILE_SIZE_MB,
                        fileName: file.name,
                    }),
                );
                setFiles(null);
                return;
            }
            setFiles(selectedFiles);
        }
    };
    return (
        <Box component="fieldset" sx={{ border: 'none', padding: 0, margin: 0 }}>
            <Box component="legend" sx={{ marginBottom: 1 }}>
                <Typography variant="subtitle1" component="span" sx={{ fontWeight: 500 }}>
                    {t('web_app.file_upload.title')}
                </Typography>
                <Typography
                    variant="caption"
                    component="div"
                    sx={{ color: 'text.secondary', marginTop: 0.25 }}
                >
                    {t('web_app.file_upload.supported_formats_label', {
                        formats: ACCEPTED_FORMATS,
                    })}
                </Typography>
            </Box>
            <label htmlFor="contained-button-file">
                <Input
                    id="contained-button-file"
                    type="file"
                    inputProps={{
                        accept: '.pdf,.docx,.txt,.xlsx,.xls',
                    }}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
                <Button
                    variant="contained"
                    component="span"
                    className="button-primary rounded"
                    disableElevation
                    disabled={disabled}
                >
                    {t('web_app.file_upload.upload_button_label')}
                </Button>
            </label>
            {error && (
                <Alert severity="error" sx={{ marginTop: 1 }} role="alert">
                    {error}
                </Alert>
            )}
            {files && !error && (
                <Box sx={{ marginTop: 1 }} role="status" aria-live="polite">
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, marginBottom: 0.5 }}>
                        {t('web_app.file_upload.selected_files_title')}
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: 0 }}>
                        {Array.from(files).map((file, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginBottom: 0.5,
                                }}
                            >
                                <Typography variant="body2" component="li">
                                    {file.name} ({(file.size / 1024).toFixed(2)} KB)
                                </Typography>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => setFiles(null)}
                                    title={t('web_app.file_upload.remove_button_label')}
                                    sx={{ ml: 0.5 }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </ul>
                </Box>
            )}
            <Typography id="file-upload-help" variant="caption" sx={{ display: 'none' }}>
                {t('web_app.file_upload.help_text', {
                    formats: ACCEPTED_FORMATS,
                    maxSize: MAX_FILE_SIZE_MB,
                })}
            </Typography>
        </Box>
    );
};
